'use client'

import { memo, useMemo, FC } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { BitmapListing } from '@/utils/hooks/useBitmap'
import { MEMPOOL_DOT_SPACE } from '@/utils/constants'
import { X } from 'lucide-react'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

// Bitmap icon component - uses Magic Eden CDN for bitmap thumbnails
const BitmapIcon: FC<{ inscriptionId: string; className?: string }> = ({
	inscriptionId,
	className = 'size-6'
}) => (
	<img
		src={`https://bitmap-img.magiceden.dev/v1/${inscriptionId}`}
		alt=""
		className={`${className} rounded object-cover flex-shrink-0`}
		loading="lazy"
	/>
)

// Shared pill component for both listing and buying modes
interface BitmapPillProps {
	blockHeight: number
	inscriptionId?: string
	priceSats?: number
	priceBTC?: number // For listing mode (BTC input)
	currentListingPrice?: number // Current listing price in sats (if already listed)
	exchangeRate?: number
	onRemove: () => void
}

const BitmapPill: FC<BitmapPillProps> = ({
	blockHeight,
	inscriptionId,
	priceSats,
	priceBTC,
	currentListingPrice,
	onRemove
}) => {
	// Calculate display values - compact format, no USD
	const btcDisplay = priceSats ? formatBtcPrice(priceSats) : priceBTC ? priceBTC.toFixed(6) : null

	const bgClass = 'bg-accent/80 hover:bg-accent/80'

	return (
		<div
			className={`flex items-center gap-1.5 ${bgClass} rounded-md px-2 py-1 flex-shrink-0 cursor-pointer group transition-colors`}
			onClick={onRemove}
			role="button"
			tabIndex={0}
			onKeyDown={onKeyboardClick(onRemove)}
		>
			{inscriptionId && <BitmapIcon inscriptionId={inscriptionId} className="size-5 rounded" />}
			<span className="text-xs text-foreground font-medium">{blockHeight}.bitmap</span>
			{btcDisplay && <span className="text-[10px] text-success">{btcDisplay}</span>}
			{currentListingPrice && !priceBTC && (
				<span className="text-[10px] text-orange-400" title="Current listing price">
					({formatBtcPrice(currentListingPrice)})
				</span>
			)}
			<X className="size-3 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
		</div>
	)
}

// Type for owned block selection (for listing)
export interface SelectedOwnedBlockInfo {
	block: BlockData
	inscription: any // Full inscription data with id
}

// Format satoshi price to BTC with appropriate decimal places
function formatBtcPrice(satoshis: number): string {
	const btc = satoshis / 100_000_000
	if (btc >= 1) {
		return btc.toFixed(4)
	} else if (btc >= 0.01) {
		return btc.toFixed(6)
	} else {
		return btc.toFixed(8)
	}
}

// Format USD price
function formatUsdPrice(satoshis: number, exchangeRate: number): string {
	const btc = satoshis / 100_000_000
	const usd = btc * exchangeRate
	if (usd >= 1000) {
		return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
	} else if (usd >= 1) {
		return `$${usd.toFixed(2)}`
	} else {
		return `$${usd.toFixed(4)}`
	}
}

export interface BlockData {
	index: number
	blockHeight: number
	hash: string
	timestamp: string
	transactions: number
	size: number
	miner: string
	difficulty: number
	isLoading?: boolean
}

// Selected block with listing info
export interface SelectedBlockInfo {
	block: BlockData
	listing?: BitmapListing
}

// Placeholder data while loading from API
export function generateBlockData(index: number): BlockData {
	return {
		index,
		blockHeight: index,
		hash: 'Loading...',
		timestamp: '',
		transactions: 0,
		size: 0,
		miner: 'Loading...',
		difficulty: 0,
		isLoading: true
	}
}

// Fetch real block data from Mempool.space API
// Uses /api/blocks/:startHeight - returns array of blocks, first one is at requested height
export async function fetchBlockData(index: number): Promise<BlockData> {
	try {
		// Single API call - returns array of 10 blocks starting at this height
		const response = await fetch(`${MEMPOOL_DOT_SPACE}/api/blocks/${index}`)
		if (!response.ok) throw new Error('Failed to fetch block data')
		const blocks = await response.json()

		// First block in array is at the requested height
		const block = blocks[0]
		if (!block || block.height !== index) {
			throw new Error('Block not found at expected height')
		}

		// Mempool API response fields:
		// id, height, timestamp, tx_count, size, weight, difficulty, extras.pool.name
		return {
			index,
			blockHeight: block.height,
			hash: block.id,
			timestamp: new Date(block.timestamp * 1000).toISOString(),
			transactions: block.tx_count,
			size: block.size,
			miner: block.extras?.pool?.name || 'Unknown',
			difficulty: block.difficulty,
			isLoading: false
		}
	} catch (error) {
		console.error('Error fetching block data:', error)
		// Return placeholder on error
		return {
			index,
			blockHeight: index,
			hash: 'Error loading',
			timestamp: '',
			transactions: 0,
			size: 0,
			miner: 'Unknown',
			difficulty: 0,
			isLoading: false
		}
	}
}

function truncateHash(hash: string, startChars = 6, endChars = 6): string {
	if (hash.length <= startChars + endChars) return hash
	return `${hash.slice(0, startChars)}...${hash.slice(-endChars)}`
}

function formatTimestamp(timestamp: string): string {
	if (!timestamp) return '—'
	try {
		const date = new Date(timestamp)
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		})
	} catch {
		return '—'
	}
}

interface BlockInfoPanelProps {
	selectedBlock: BlockData | null
	selectedBlocks?: SelectedBlockInfo[]
	listing?: BitmapListing
	listingsMap?: Map<number, BitmapListing> // For checking if owned blocks are already listed
	onAddToCart?: (inscriptionId: string) => void
	onRemoveBlock?: (blockIndex: number) => void
	onClose?: () => void
	onBuyMultiple?: () => void
	onBuySingle?: (blockHeight: number, listing: BitmapListing) => void
	// Listing mode props
	selectedOwnedBlocks?: SelectedOwnedBlockInfo[]
	onRemoveOwnedBlock?: (blockIndex: number) => void
	onListMultiple?: () => void
	onDelistMultiple?: () => void
	listingPrices?: Record<number, number>
	onPriceChange?: (blockIndex: number, price: number) => void
	globalListingPrice?: number | null
	onGlobalListingPriceChange?: (price: number | null) => void
	floorPrice?: number // Floor price in satoshis
	// For single owned block
	isOwnedBlock?: boolean
	onListSingle?: (blockHeight: number, inscription: any) => void
}

export const BlockInfoPanel = memo(function BlockInfoPanel({
	selectedBlock,
	selectedBlocks = [],
	listing,
	listingsMap,
	onAddToCart: _onAddToCart,
	onRemoveBlock,
	onClose,
	onBuyMultiple,
	onBuySingle,
	// Listing mode props
	selectedOwnedBlocks = [],
	onRemoveOwnedBlock,
	onListMultiple,
	onDelistMultiple,
	listingPrices = {},
	onPriceChange: _onPriceChange,
	globalListingPrice,
	onGlobalListingPriceChange,
	floorPrice,
	isOwnedBlock,
	onListSingle: _onListSingle
}: BlockInfoPanelProps) {
	const { exchangeRate } = useExchangeRate()

	// Calculate totals for multi-select (buying)
	const { totalPrice, listedBlocks } = useMemo(() => {
		const listed = selectedBlocks.filter((sb) => sb.listing)
		const total = listed.reduce((sum, sb) => sum + (sb.listing?.satoshiPrice || 0), 0)
		return {
			totalPrice: total,
			listedBlocks: listed
		}
	}, [selectedBlocks])

	// Calculate if listing prices are valid
	const { hasValidPrices, totalListingPrice } = useMemo(() => {
		if (selectedOwnedBlocks.length === 0) return { hasValidPrices: false, totalListingPrice: 0 }

		let total = 0
		let allValid = true

		for (const sb of selectedOwnedBlocks) {
			const price = listingPrices[sb.block.index] ?? globalListingPrice
			const priceSats = price ? price * 100_000_000 : 0
			if (!price || price <= 0) {
				allValid = false
			} else {
				total += priceSats
			}
		}

		return { hasValidPrices: allValid, totalListingPrice: total }
	}, [selectedOwnedBlocks, listingPrices, globalListingPrice])

	// Calculate how many selected owned blocks are already listed
	const alreadyListedCount = useMemo(() => {
		if (!listingsMap) return 0
		return selectedOwnedBlocks.filter((sb) => listingsMap.has(sb.block.index)).length
	}, [selectedOwnedBlocks, listingsMap])

	// LISTING MODE: If we have owned blocks selected for listing
	if (selectedOwnedBlocks.length > 0) {
		return (
			<div className="fixed bottom-8 left-0 right-0 z-40 pointer-events-none">
				<div className="w-full pointer-events-auto">
					<div className="bg-background/90 backdrop-blur-xl border-t border-border shadow-2xl shadow-black/50">
						<div className="px-4 py-2">
							{/* Global price input */}
							<div className="flex items-center gap-2 mb-2">
								<span className="text-xs text-muted-foreground">Set price (BTC):</span>
								<Input
									type="number"
									step="0.0001"
									min="0"
									placeholder="0.001"
									value={globalListingPrice ?? ''}
									onChange={(e) => {
										const val = e.target.value ? parseFloat(e.target.value) : null
										onGlobalListingPriceChange?.(val)
									}}
									className="w-32 h-7 text-xs bg-accent border-border"
								/>
								{floorPrice && floorPrice > 0 && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => onGlobalListingPriceChange?.(floorPrice / 1e8)}
										className="h-7 px-2 text-xs bg-accent border-border hover:bg-accent"
									>
										Floor ({(floorPrice / 1e8).toFixed(6)})
									</Button>
								)}
							</div>

							{/* Mobile: stacked layout, Desktop: single row */}
							<div className="flex flex-col md:flex-row md:items-center gap-2">
								{/* Selected bitmaps list - horizontal scrollable */}
								<div className="flex items-center gap-1.5 flex-1 overflow-x-auto py-0.5">
									{selectedOwnedBlocks.map((sb) => {
										const price = listingPrices[sb.block.index] ?? globalListingPrice
										const currentListing = listingsMap?.get(sb.block.index)
										return (
											<BitmapPill
												key={sb.block.index}
												blockHeight={sb.block.blockHeight}
												inscriptionId={sb.inscription?.id}
												priceBTC={price ?? undefined}
												currentListingPrice={currentListing?.satoshiPrice}
												onRemove={() => onRemoveOwnedBlock?.(sb.block.index)}
											/>
										)
									})}
								</div>

								{/* Divider - desktop only */}
								<div className="hidden md:block h-8 w-px bg-white/10 flex-shrink-0" />

								{/* List/Delist buttons - full width on mobile */}
								<div className="flex items-center gap-2 w-full md:w-auto md:flex-shrink-0">
									{hasValidPrices && (
										<div className="text-left md:text-right flex-shrink-0">
											<div className="text-sm font-bold text-foreground">
												{formatBtcPrice(totalListingPrice)}{' '}
												<span className="text-muted-foreground text-xs">BTC</span>
											</div>
										</div>
									)}
									<Button
										onClick={onListMultiple}
										disabled={!hasValidPrices}
										className="bg-violet-600 hover:bg-violet-800 text-white font-bold px-3 h-8 text-xs flex-1 md:flex-none disabled:opacity-50"
									>
										List {selectedOwnedBlocks.length}
									</Button>

									{/* Delist button - only show if any selected are already listed */}
									{alreadyListedCount > 0 && onDelistMultiple && (
										<Button
											onClick={onDelistMultiple}
											variant="outline"
											className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20 font-bold px-3 h-8 text-xs flex-1 md:flex-none"
										>
											Delist {alreadyListedCount}
										</Button>
									)}

									{/* Close/Clear button */}
									{onClose && (
										<button
											onClick={onClose}
											className="size-8 rounded-lg bg-white/5 hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
											title="Clear selection"
										>
											<X className="size-4" />
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// PURCHASE MODE: If we have multiple selections for buying, show simple cart-style UI
	if (selectedBlocks.length > 0 && listedBlocks.length > 0) {
		return (
			<div className="fixed bottom-8 left-0 right-0 z-40 pointer-events-none">
				<div className="w-full pointer-events-auto">
					<div className="bg-background/90 backdrop-blur-xl border-t border-border shadow-2xl shadow-black/50">
						<div className="px-4 py-2">
							{/* Mobile: stacked layout, Desktop: single row */}
							<div className="flex flex-col md:flex-row md:items-center gap-2">
								{/* Selected bitmaps list - horizontal scrollable */}
								<div className="flex items-center gap-1.5 flex-1 overflow-x-auto py-0.5">
									{listedBlocks.map((sb) => (
										<BitmapPill
											key={sb.block.index}
											blockHeight={sb.block.blockHeight}
											inscriptionId={sb.listing?.inscriptionId}
											priceSats={sb.listing?.satoshiPrice}
											onRemove={() => onRemoveBlock?.(sb.block.index)}
										/>
									))}
								</div>

								{/* Divider - desktop only */}
								<div className="hidden md:block h-8 w-px bg-white/10 flex-shrink-0" />

								{/* Price and Buy - full width on mobile */}
								<div className="flex items-center gap-3 w-full md:w-auto md:flex-shrink-0">
									<div className="text-left md:text-right flex-shrink-0">
										<div className="text-sm font-bold text-foreground">
											{formatBtcPrice(totalPrice)}{' '}
											<span className="text-muted-foreground text-xs">BTC</span>
										</div>
										{exchangeRate && (
											<div className="text-[10px] text-muted-foreground">
												{formatUsdPrice(totalPrice, exchangeRate)}
											</div>
										)}
									</div>
									<Button
										onClick={onBuyMultiple}
										className="bg-violet-600 hover:bg-violet-800 text-white font-bold px-4 h-9 text-sm flex-1 md:flex-none md:min-w-[160px]"
									>
										Buy {listedBlocks.length} Inscription{listedBlocks.length !== 1 ? 's' : ''}
									</Button>

									{/* Close/Clear button */}
									{onClose && (
										<button
											onClick={onClose}
											className="size-8 rounded-lg bg-white/5 hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
											title="Clear selection"
										>
											<X className="size-4" />
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Single block selection (original behavior)
	if (!selectedBlock) {
		return null
	}

	const handleBuy = () => {
		if (!listing || !selectedBlock) return
		if (onBuySingle) {
			onBuySingle(selectedBlock.blockHeight, listing)
		}
	}

	return (
		<div className="fixed bottom-8 left-0 right-0 z-40 pointer-events-none">
			<div className="w-full pointer-events-auto">
				<div className="bg-background/90 backdrop-blur-xl border-t border-border shadow-2xl shadow-black/50">
					<div className="px-4 py-3">
						<div className="flex items-center justify-between gap-3">
							{/* Left: Block info */}
							<div className="flex items-center gap-3 min-w-0 flex-1">
								{/* Block number and status */}
								<div className="min-w-0">
									<div className="flex items-center gap-2">
										<span className="text-sm font-bold text-foreground">
											Block {selectedBlock.blockHeight.toLocaleString()}
										</span>
										{isOwnedBlock ? (
											<Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/40 text-[10px] px-1.5 py-0">
												Owned
											</Badge>
										) : listing ? (
											<Badge className="bg-success/20 text-success border-success/30 text-[10px] px-1.5 py-0">
												For Sale
											</Badge>
										) : (
											<Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] px-1.5 py-0">
												Not Listed
											</Badge>
										)}
									</div>

									{/* Stats - compact row, hidden on very small screens */}
									{!selectedBlock.isLoading && (
										<div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
											<a
												href={`${MEMPOOL_DOT_SPACE}/block/${selectedBlock.hash}`}
												target="_blank"
												rel="noopener noreferrer"
												className="text-cyan-400 hover:text-cyan-300 font-mono hidden sm:inline"
											>
												{truncateHash(selectedBlock.hash, 4, 4)}
											</a>
											<span className="hidden md:inline">
												{selectedBlock.transactions.toLocaleString()} txns
											</span>
											<span className="hidden md:inline">
												{(selectedBlock.size / 1000000).toFixed(2)} MB
											</span>
											<span>{formatTimestamp(selectedBlock.timestamp)}</span>
										</div>
									)}

									{selectedBlock.isLoading && (
										<div className="text-muted-foreground text-xs mt-1 animate-pulse">
											Loading…
										</div>
									)}
								</div>
							</div>

							{/* Right: Price/Buy or List or Close */}
							<div className="flex items-center gap-2 flex-shrink-0">
								{listing && !isOwnedBlock && (
									<>
										<div className="text-right hidden sm:block">
											<div className="text-sm font-bold text-success">
												{formatBtcPrice(listing.satoshiPrice)} BTC
											</div>
											<div className="text-[10px] text-muted-foreground">
												{exchangeRate ? formatUsdPrice(listing.satoshiPrice, exchangeRate) : '—'}
											</div>
										</div>
										<Button
											onClick={handleBuy}
											className="bg-violet-600 hover:bg-violet-800 text-white font-bold px-3 h-8 text-xs"
										>
											Buy
										</Button>
									</>
								)}

								{onClose && (
									<button
										onClick={onClose}
										className="size-8 rounded-lg bg-white/5 hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
									>
										<X className="size-4" />
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
})
